import React, { useEffect, useState } from "react";
import { ModalProps, RequestDetails } from "./Types";
import { getAnsweredRequest, displayNameInfo } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";

type ClassType = "First" | "Economy" | "Eco+" | "Business" | "NonRev";

const RequestReviewTab: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  airlineId,
  requestDetailId,
  logoImg,
  airlineName,
  flightDate,
  departureAirport,
  flightNumber,
  departureTime,
  flightDuration,
  arrivalAirport,
  arrivalTime,
}) => {
  const [requestData, setRequestData] = useState<RequestDetails | null>(null);
  const [focusedClass, setFocusedClass] = useState<ClassType | null>(null);
  const [responseName, setResponseName] = useState<any>(null);
  const { showLoader, hideLoader } = useGlobalLoader();

  useEffect(() => {
    if (!isOpen || !requestDetailId) return;
    const fetchRequestData = async () => {
      showLoader();
      try {
        const response = await getAnsweredRequest(requestDetailId);
        setRequestData(response);
      } catch (err) {
        console.error("Failed to load request details", err);
      } finally {
        hideLoader();
      }
    };
    fetchRequestData();
  }, [isOpen, requestDetailId]);

  useEffect(() => {
    if (requestData?.userId) {
      const fetchDisplayName = async () => {
        try {
          const response = await displayNameInfo(requestData.userId);
          setResponseName(response);
          // console.log("Display Name Response:", response.displayName);
        } catch (err) {
          console.error("Failed to load display name", err);
        }
      };
      fetchDisplayName();
    }
  }, [requestData?.userId]);

  // Determine if value is overbooked
  const isOverbooked = (value: number | null): boolean => {
    return value != null && value < 0;
  };

  // Get appropriate input class
  const getInputClass = (classLabel: ClassType): string => {
    const value = getClassValue(classLabel);
    const isCurrentlyFocused = focusedClass === classLabel;

    let baseClasses =
      "w-full text-center h-12 border rounded-md text-lg font-bold cursor-pointer focus:outline-none focus:ring-2 focus:ring-black";

    // Apply overbooked styling (red background and text)
    if (isOverbooked(value)) {
      baseClasses += " bg-red-100 text-red-600";
    }

    // Apply border styling - black border when focused takes precedence
    if (isCurrentlyFocused) {
      baseClasses += " border-black border-2";
    } else if (isOverbooked(value)) {
      baseClasses += " border-red-500";
    } else {
      baseClasses += " border-lightgray-100 text-black";
    }

    return baseClasses;
  };

  // Map class label to actual data value
  const getClassValue = (classLabel: ClassType): number | null => {
    switch (classLabel) {
      case "First":
        return requestData?.firstClass ?? null;
      case "Economy":
        return requestData?.eco ?? null;
      case "Eco+":
        return requestData?.ecoPlus ?? null;
      case "Business":
        return requestData?.busClass ?? null;
      default:
        return null;
    }
  };

  // Check if more seats available toggle should be checked
  const isMoreSeatsAvailable = (classLabel: ClassType): boolean => {
    switch (classLabel) {
      case "First":
        return !!requestData?.moreFirstClass;
      case "Economy":
        return !!requestData?.moreEco;
      case "Eco+":
        return !!requestData?.moreEcoPlus;
      case "Business":
        return !!requestData?.moreBusClass;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      id="default-modal1"
      tabIndex={-1}
      aria-hidden={!isOpen}
      className="fixed p-8 top-0 right-0 left-0 z-50 md:inset-0 flex justify-center items-center w-full h-full bg-black bg-opacity-50"
    >
      <div className="relative p-6 w-full max-w-xl my-8">
        <div className="relative bg-white max-h-[90vh] overflow-y-auto rounded-2xl shadow">
          {/* Modal Header */}
          <div className="flex justify-between px-6 pt-6 rounded-t">
            <button
              type="button"
              className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
              onClick={onClose}
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>

          {/* Flight Info */}
          <div className="p-6 mt-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                className="w-10"
                src={logoImg}
                alt={airlineName}
              />
              <div>
                <p className="text-lightgray-200 text-xs">Airlines</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {airlineName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <p className="text-lightgray-200 text-xs">Flight No.</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {flightNumber}
                </p>
              </div>
              <div>
                <p className="text-lightgray-200 text-xs">Date</p>
                <p className="text-grease-100 mt-1 text-sm font-medium">
                  {flightDate}
                </p>
              </div>
            </div>
          </div>

          {/* Flight Path Visualization */}
          <div className="bg-sky-100 bg-opacity-40 rounded-xl p-6 mx-6 mb-6">
            <div className="flex items-center justify-between">
              {/* Departure */}
              <div className="flex flex-col gap-1">
                <p className="text-grease-100 text-sm font-medium">
                  {departureAirport}
                </p>
                <div className="flex items-center gap-1">
                  <i className="st-arrow-slant text-gray-500 font-medium"></i>
                  <p className="text-lightgray-200 text-xs">{departureTime}</p>
                </div>
              </div>

              {/* Centered Flight Duration */}
              <div className="w-full flex flex-col items-center">
                <div className="flex items-center w-full">
                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                    </div>
                  </div>
                  <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                  <i className="st-flight-slim text-royalblue-100 text-base"></i>
                  <div className="w-full border-b-2 border-dashed border-indigo-200"></div>
                  <div className="w-3 h-3 bg-blue-100 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full flex items-center justify-center">
                      <div className="w-1 h-1 bg-royalblue-100 rounded-full"></div>
                    </div>
                  </div>
                </div>
                <p className="text-lightgray-150 text-xs text-center mt-2">
                  {flightDuration}
                </p>
              </div>

              {/* Arrival */}
              <div className="flex flex-col items-end gap-1">
                <p className="text-grease-100 text-sm font-medium">
                  {arrivalAirport}
                </p>
                <div className="flex items-center gap-1">
                  <i className="st-arrow-slant text-gray-500 font-medium rotate-90"></i>
                  <p className="text-lightgray-200 text-xs">{arrivalTime}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Seat Inputs */}
          <div className="flex items-center justify-between gap-4 px-6 pb-6">
            {["First", "Economy", "Eco+", "Business"].map((label) => (
              <div
                key={label}
                className={getInputClass(label as ClassType)}
                onClick={() => setFocusedClass(label as ClassType)}
              >
                <p
                  className={`text-grease-100 pt-1 font-medium text-sm ${isOverbooked(getClassValue(label as ClassType)) ? "text-red-600" : ""}`}
                >
                  {getClassValue(label as ClassType) ?? 0}
                </p>
                <p className="text-lightgray-150 text-xs">
                  {label === "Business" ? "Bus" : label}
                </p>
              </div>
            ))}
          </div>

          {/* Listed Passengers Input */}
          <div className="flex flex-col items-center justify-between gap-3 mb-8 mt-2 px-6">
            <p className="font-medium text-sm text-grease-100">
              Listed Non Rev Passengers
            </p>
            <div
              className={`w-64 mx-auto text-center h-12 border rounded-lg flex flex-col justify-center cursor-pointer ${
                focusedClass === "NonRev"
                  ? "border-black border-2"
                  : "border-lightgray-100"
              }`}
              onClick={() => setFocusedClass("NonRev")}
            >
              <p className="text-grease-100 font-medium text-sm">
                {requestData?.nonRevListed ?? 0}
              </p>
              <p className="text-lightgray-150 text-xs">Non Rev</p>
            </div>
          </div>

          {/* Toggle Section - Only shown when a class is focused AND it's not NonRev */}
          {focusedClass && focusedClass !== "NonRev" && (
            <div className="border border-blue-100 bg-white rounded-xl p-6 mx-6 mb-6">
              {/* Overbooked Toggle */}
              <div className="py-4 border-b border-blue-100">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isOverbooked(getClassValue(focusedClass))}
                    readOnly
                  />
                  <div
                    style={{ width: "2.2rem" }}
                    className="relative h-5 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"
                  ></div>
                  <span className="ms-3 text-sm font-thin text-lightgray-200">
                    Overbooked
                  </span>
                </label>
              </div>

              {/* This flight has no... Toggle */}
              <div className="py-4 border-b border-blue-100">
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={getClassValue(focusedClass) === 0}
                    readOnly
                  />
                  <div
                    style={{ width: "2.2rem" }}
                    className="relative h-5 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"
                  ></div>
                  <span className="ms-3 text-sm font-thin text-lightgray-200">
                    This flight has no {focusedClass} class
                  </span>
                </label>
              </div>

              {/* More Seats Available Toggle */}
              <div className="pt-4">
                <label className="flex items-center cursor-pointer mb-5">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isMoreSeatsAvailable(focusedClass)}
                    readOnly
                  />
                  <div
                    style={{ width: "2.2rem" }}
                    className="relative h-5 bg-blue-100 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 flex-shrink-0"
                  ></div>
                  <span className="ms-3 text-sm font-thin text-gray-500 flex-1">
                    There are more seats available in {focusedClass} class but I
                    don't know how many
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Only show Non-Rev Pax Per Class when focused on Non-Rev */}
          {focusedClass === "NonRev" && (
            <div className="bg-sky-100 bg-opacity-40 rounded-xl mx-6 mb-6">
              <p className="font-medium text-sm text-gray-700 mb-4 mt-4 text-center">
                Listed Non-Rev Pax Per Class
              </p>
              <div className="py-4 px-4 bg-sky-200 bg-opacity-30 rounded-b-xl">
                <div className="flex items-center justify-between gap-4 px-6 pb-6">
                  {[
                    { label: "First", value: requestData?.nonFirstClass },
                    { label: "Eco", value: requestData?.nonEco },
                    { label: "Eco+", value: requestData?.nonEcoPlus },
                    { label: "Bus", value: requestData?.nonBusClass },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex-1 flex flex-col items-center justify-center text-center h-12 border border-lightgray-150 rounded-lg"
                    >
                      <p className="text-grease-100 font-medium text-sm">
                        {item.value ?? 0}
                      </p>
                      <p className="text-lightgray-200 text-xs">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="px-6">
            <label className="block text-sm font-medium text-grease-100 mb-2 mx-6">
              Requested By
            </label>
            <div className="bg-white rounded-xl mx-6">
              <p className="text-grease-100 text-sm">
                {responseName?.displayName || "Anonymous User"}
              </p>
            </div>
          </div>
          {/* Comment Section */}
          <div className="bg-white rounded-xl p-6 mx-6 mb-6 mt-3">
            <label className="block text-sm font-medium text-grease-100 mb-2">
              Comment
            </label>
            <textarea
              value={requestData?.comments || ""}
              disabled
              placeholder="No comment provided"
              rows={3}
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 resize-none ${
                requestData?.comments ? "bg-white" : "bg-gray-50 text-gray-400"
              }`}
            />
          </div>

          {/* Close Button
          <div className="flex gap-4 items-center justify-end p-6 rounded-b">
            <button onClick={onClose} className="flex items-center gap-2 p-2 bg-indigo-100 rounded-lg">
              <p className="font-medium text-grease-50 text-sm px-3">Cancel</p>
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default RequestReviewTab;
